import { useState, useEffect, useCallback, useMemo } from 'react';
import { http } from '@/lib/http';
import { toast } from 'sonner';
import { FileNode } from '@/pages/workspace/types';
import { useProjectsStore } from '@/stores/projects';
import { useWorkspaceStore } from '@/stores/workspace';

export const useFileTree = () => {
  const { activeProjectId } = useProjectsStore();
  const loadFileTree = useWorkspaceStore((state) => state.loadFileTree);

  useEffect(() => {
    if (activeProjectId) {
      loadFileTree(".").catch((error) => {
        toast.error("Failed to load file tree", {
          description: error.message,
        });
      });
    }
  }, [activeProjectId, loadFileTree]);

  return { loadFileTree };
};

export const useProcessedFileTree = (fileTree: FileNode[]) => {
  return useMemo(() => {
    const aggregate = (nodes: FileNode[]): FileNode[] => {
      return nodes.map((node) => {
        if (node.type === 'directory' && node.children?.length) {
          const processedChildren = aggregate(node.children);

          let totalLines = 0;
          let coveredLines = 0;
          let totalBranches = 0;
          let coveredBranches = 0;
          let totalFuncs = 0;
          let coveredFuncs = 0;
          let coverageDesynced = false;

          let totalTests = 0;
          let passingTests = 0;
          let failingTests = 0;
          let flakyTests = 0;
          let latestRunId: string | undefined = undefined;

          processedChildren.forEach((child) => {
            if (child.coverage?.summary) {
              totalLines += child.coverage.summary.lines.total;
              coveredLines += child.coverage.summary.lines.covered;
              totalBranches += child.coverage.summary.branches.total;
              coveredBranches += child.coverage.summary.branches.covered;
              totalFuncs += child.coverage.summary.functions.total;
              coveredFuncs += child.coverage.summary.functions.covered;
              if (child.coverageDesynced) {
                coverageDesynced = true;
              }
            }
            if (child.testSummary) {
              totalTests += child.testSummary.total;
              passingTests += child.testSummary.passing;
              failingTests += child.testSummary.failing;
              flakyTests += child.testSummary.flaky || 0;
              if (child.testSummary.runId) {
                latestRunId = child.testSummary.runId;
              }
            }
          });

          const newNode = { ...node, children: processedChildren };

          if (totalLines > 0 || totalBranches > 0 || totalFuncs > 0) {
            newNode.coverage = {
              path: node.path,
              summary: {
                lines: {
                  total: totalLines,
                  covered: coveredLines,
                  skipped: 0,
                  pct: totalLines > 0 ? (coveredLines / totalLines) * 100 : 0,
                },
                functions: {
                  total: totalFuncs,
                  covered: coveredFuncs,
                  skipped: 0,
                  pct: totalFuncs > 0 ? (coveredFuncs / totalFuncs) * 100 : 0,
                },
                branches: {
                  total: totalBranches,
                  covered: coveredBranches,
                  skipped: 0,
                  pct: totalBranches > 0 ? (coveredBranches / totalBranches) * 100 : 0,
                },
              },
              line_hits: {},
            };
            newNode.coverageDesynced = coverageDesynced;
          }

          if (totalTests > 0) {
            newNode.testSummary = {
              total: totalTests,
              passing: passingTests,
              failing: failingTests,
              flaky: flakyTests,
              runId: latestRunId,
            };
          }

          return newNode;
        }
        return node;
      });
    };
    return aggregate(fileTree);
  }, [fileTree]);
};

export const useFilteredFileTree = (processedFileTree: FileNode[], filterQuery: string) => {
  return useMemo(() => {
    if (!filterQuery.trim()) {
      return processedFileTree;
    }

    const lowerCaseQuery = filterQuery.toLowerCase();
    const wildcard = lowerCaseQuery.startsWith('*') ? lowerCaseQuery.substring(1) : null;
    const directorySearch = lowerCaseQuery.endsWith('/') ? lowerCaseQuery : null;

    const isMatch = (node: FileNode): boolean => {
      if (wildcard) {
        return node.type === 'file' && node.name.toLowerCase().endsWith(wildcard);
      }
      if (directorySearch) {
        return node.path.toLowerCase().startsWith(directorySearch);
      }
      return node.name.toLowerCase().includes(lowerCaseQuery);
    };

    function filter(items: FileNode[]): FileNode[] {
      const result: FileNode[] = [];
      for (const node of items) {
        if (node.type === 'file') {
          if (isMatch(node)) {
            result.push(node);
          }
        } else if (node.type === 'directory') {
          if (!directorySearch && isMatch(node)) {
            result.push(node);
            continue;
          }
          const filteredChildren = node.children ? filter(node.children) : [];
          if (filteredChildren.length > 0) {
            result.push({ ...node, children: filteredChildren });
          }
        }
      }
      return result;
    }
    return filter(processedFileTree);
  }, [processedFileTree, filterQuery]);
};